<template>
  <!-- Two separate Teleports — Vue 3 Teleport with mixed Transition
       siblings on a v-if can mispatch the comment/element placeholder
       on initial mount. Splitting keeps each Transition in its own
       single-root Teleport, which is the well-tested path. -->
  <Teleport to="body">
    <!-- Visualizer: smooth gradient line driven by the current track's
         peaks data, windowed around the playback position. Lives
         outside the shell so it isn't clipped by max-width/padding. -->
    <Transition name="viz-fade">
      <canvas
        v-if="vizActive"
        ref="vizCanvasRef"
        class="player-viz"
        aria-hidden="true"
      ></canvas>
    </Transition>
  </Teleport>
  <!-- Smoke lives in its OWN Teleport so its stacking context is
       independent from the shell. The shell sits at z-index 9999
       (visualizer at 9998), and any descendant of the shell — no
       matter its local z-index — paints above the viz because the
       shell itself owns a z-index 9999 stacking context. Hoisting
       the smoke up to body-level lets us slot it cleanly behind the
       visualizer (z-index 9996 < 9998). The cover-driven colour is
       passed via the same `--player-accent-channels` custom property
       set on this wrapper. -->
  <Teleport to="body">
    <Transition name="smoke-fade">
      <div
        v-if="currentTrack"
        class="player-smoke"
        :style="{ '--player-accent-channels': primaryChannels }"
        aria-hidden="true"
      >
        <span class="smoke-blob smoke-blob-1"></span>
        <span class="smoke-blob smoke-blob-2"></span>
        <span class="smoke-blob smoke-blob-3"></span>
        <span class="smoke-blob-4"></span>
        <span class="smoke-blob-5"></span>
      </div>
    </Transition>
  </Teleport>
  <Teleport to="body">
    <div
      class="shop-player-shell"
      :class="{ playing: isPlaying }"
      :style="{ '--player-accent-channels': primaryChannels }"
    >
      <Transition name="player-slide">
        <div v-if="currentTrack" class="shop-player">
        <!-- Ambient backdrop: blurred artwork bleeds through the glass to
             tint the player with the cover's dominant colors. -->
        <div class="player-ambient" aria-hidden="true">
          <img v-if="currentTrack.imageUrl" :src="artworkSrc" alt="" />
          <div class="player-ambient-veil"></div>
        </div>

        <!-- Glow ring that pulses while playing. -->
        <div class="player-aura" aria-hidden="true"></div>

        <!-- HUD corner brackets — rim-idle, cover-accent hot while
             playing. Purely decorative. -->
        <span class="player-bracket player-bracket--tr" aria-hidden="true"></span>
        <span class="player-bracket player-bracket--bl" aria-hidden="true"></span>

        <!-- Left: artwork + track meta -->
        <div class="player-track">
          <div class="player-art">
            <img v-if="currentTrack.imageUrl" :src="artworkSrc" :alt="currentTrack.title" />
            <div v-else class="player-art-fallback">
              <svg viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55C7.79 13 6 14.79 6 17s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
            </div>
          </div>
          <div class="player-meta">
            <div class="player-title-row">
              <div class="player-eq" :class="{ on: isPlaying }" aria-hidden="true">
                <span></span><span></span><span></span><span></span>
              </div>
              <div class="player-title" :title="currentTrack.title">{{ currentTrack.title }}</div>
            </div>
            <div class="player-artist">{{ hasPreviewBounds ? $t('player.preview') : (currentTrack?.artist || '') }}</div>
          </div>
        </div>

        <!-- Center: transport + waveform -->
        <div class="player-main">
          <div class="player-controls">
            <button class="ctrl loop" :class="{ active: looping }" @click="looping = !looping" :title="$t('player.loop') || 'Loop'">
              <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/></svg>
            </button>
            <button class="ctrl play" @click="player.toggle(currentTrack)" :title="isPlaying ? 'Pause' : 'Play'">
              <span class="play-ring"></span>
              <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 ml-0.5"><path d="M8 5v14l11-7z"/></svg>
              <svg v-else viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
            </button>
            <button class="ctrl stop" @click="player.stop" title="Stop">
              <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><rect x="6" y="6" width="12" height="12" rx="1.5"/></svg>
            </button>
          </div>

          <div class="player-progress">
            <span class="time-label">{{ formatTime(hasPreviewBounds ? Math.max(0, currentTime - previewStart) : currentTime) }}</span>
            <div class="waveform-wrap">
              <ClientOnly>
                <SharedWaveformCanvas
                  v-if="!isLoadingPeaks"
                  :peaks="peaksData"
                  :progress="waveProgress"
                  :region-start="previewRegionStart"
                  :region-end="previewRegionEnd"
                  region-scrim="transparent"
                  :height="40"
                  :bar-width="2"
                  :gap="1"
                  color="rgba(255,255,255,0.18)"
                  :active-color="primaryColor"
                  @seek="handleSeek"
                />
                <div v-else class="waveform-loading"></div>
              </ClientOnly>
            </div>
            <span class="time-label">{{ formatTime(hasPreviewBounds ? previewLength : duration) }}</span>
          </div>
        </div>

        <!-- Right: extras -->
        <div class="player-extras">
          <div class="vol">
            <button class="ctrl ghost" @click="toggleMute" :title="volume === 0 ? 'Unmute' : 'Mute'">
              <svg v-if="volume === 0" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
              <svg v-else-if="volume < 0.5" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/></svg>
              <svg v-else viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
            </button>
            <input
              type="range"
              class="vol-range"
              min="0" max="1" step="0.01"
              v-model.number="volume"
              :aria-label="$t('player.volume') || 'Volume'"
              :style="{ '--vol-pct': (volume * 100) + '%' }"
              @input="onVolumeInput"
            />
          </div>

          <!-- Queue / history / playlists drawer trigger. The badge
               pulses every time a track lands in history, drawing
               attention to "your listening" without nagging. The
               click handler hands its bounding-rect centre to the
               drawer so the spawn animation erupts from THIS icon. -->
          <button
            ref="queueBtnRef"
            type="button"
            class="ctrl ghost queue-btn"
            :class="{ active: player.drawerOpen.value, 'pulse': pulseQueueBtn }"
            :title="player.drawerOpen.value ? 'Close queue' : 'Open queue'"
            @click="onQueueClick($event)"
          >
            <NuxtIcon name="mdi:playlist-music" />
            <span v-if="player.queue.value.length > 0" class="queue-badge">
              {{ player.queue.value.length }}
            </span>
          </button>

          <button class="ctrl ghost close" @click="closePlayer" :title="$t('player.close') || 'Close'">
            <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

const player = useAudioPlayer()
const { currentTrack, isPlaying, currentTime, duration, volume } = player

// ── Queue drawer + history pulse ──────────────────────────────
// Tiny animation hook: when something lands in history, briefly pulse
// the queue button so the user notices the list has new content. The
// composable bumps `historyPulseAt` on each log; we watch that and
// flip a local class for ~700 ms.
const pulseQueueBtn = ref(false)
watch(player.historyPulseAt, () => {
  pulseQueueBtn.value = true
  setTimeout(() => { pulseQueueBtn.value = false }, 700)
})
const queueBtnRef = ref(null)
const onQueueClick = (ev) => {
  // Capture the button's centre at click-time so the drawer's spawn
  // animation erupts from the icon the user actually clicked. The
  // composable stores this on `drawerOrigin` for the panel to read.
  const btn = ev?.currentTarget
    || (queueBtnRef.value?.$el || queueBtnRef.value)
  if (btn && typeof btn.getBoundingClientRect === 'function') {
    const r = btn.getBoundingClientRect()
    player.toggleDrawer('queue', {
      x: r.left + r.width / 2,
      y: r.top + r.height / 2
    })
  } else {
    player.toggleDrawer('queue')
  }
}

// Waveform's `active-color` needs a parseable CSS color string (canvas
// fillStyle), not a CSS var. We resolve --primary at mount so tenants
// with non-cyan branding theme the waveform too. Default cyan keeps the
// pre-mount paint matching the tenant token in 99% of cases.
const tenantPrimaryHex = ref('#00f2ff')
// Cover-derived accent. Populated by `sampleDominantColor()` whenever
// the current track's artwork loads under CORS. Null = no sample yet
// (taint, fetch error, no artwork), and the visualizer + smoke fall
// back to the tenant primary so the player never goes uncoloured.
const dominantColor = ref(null)
// Effective viz / smoke colour. The waveform / line / ambient pull
// from this so tracks with known cover-derived colours instantly tint
// the whole bottom bar to match. Falls back to the tenant primary.
const primaryColor = computed(() => dominantColor.value || tenantPrimaryHex.value)
// CSS-bindable form used by the smoke + ambient gradients. The smoke
// uses `rgb(... / opacity)` colour-mix shapes that need plain rgb()
// values, so we normalise the resolved colour into an "r, g, b" tuple
// the template can splice into rgba()/rgb() declarations cleanly.
const primaryChannels = computed(() => hexOrRgbToChannels(primaryColor.value))

const audioElement = ref(null)
const peaksData = ref([])
const isLoadingPeaks = ref(false)
const looping = ref(false)
const previousVolume = ref(1)

// ── Visualizer (peaks-driven scrolling line) ───────────────────
// We deliberately do NOT use Web Audio's AnalyserNode here, because
// `createMediaElementSource()` silences any audio element whose source
// is cross-origin without CORS — and our `/api/assets/serve` proxy
// 302-redirects to S3 (cross-origin). Adding `crossOrigin='anonymous'`
// would block the audio entirely on buckets without an ACAO header.
// So instead we drive the line from the static `peaksData` array
// (the actual track waveform) windowed around `currentTime`. Always
// works, no CORS dependency, and the line still tracks the track's
// loudness profile because the peaks ARE the track's energy.
const vizCanvasRef = ref(null)
const vizActive = ref(false)
let vizRafId = null
let vizMotionAllowed = true
// Per-bin smoothed values so the line eases between adjacent peaks
// instead of stepping. Resized in step with the canvas width.
let vizSmoothed = null

const artworkSrc = computed(() => {
  const t = currentTrack.value
  if (!t?.imageUrl) return ''
  return t.type === 'suno' ? t.imageUrl : getProxiedUrl(t.imageUrl)
})

const closePlayer = () => {
  player.stop()
  currentTrack.value = null
}

const toggleMute = () => {
  if (volume.value > 0) {
    previousVolume.value = volume.value
    volume.value = 0
  } else {
    volume.value = previousVolume.value || 1
  }
}

const onVolumeInput = () => {
  // Keep previousVolume in sync with the user's chosen level so the
  // mute toggle restores to where they actually were, not to the prior
  // pre-mute value.
  if (volume.value > 0) previousVolume.value = volume.value
}

const formatTime = (seconds) => {
  if (!seconds) return '0:00'
  const min = Math.floor(seconds / 60)
  const sec = Math.floor(seconds % 60)
  return `${min}:${sec.toString().padStart(2, '0')}`
}

const hasPreviewBounds = computed(() => !!currentTrack.value?.previewLength)
const previewStart = computed(() => currentTrack.value?.previewStart || 0)
const previewLength = computed(() => currentTrack.value?.previewLength || 0)
const previewEnd = computed(() => previewStart.value + previewLength.value)
// Where the preview slice sits inside the FULL track. The audio element is
// fed only the window (see /api/shop/preview/:id), so its clock starts at 0
// and previewStart/previewEnd address the slice — but the waveform still
// draws the whole song, so anything measured against it has to add the
// offset back. Zero for anything that plays a complete file.
const previewOffset = computed(() => currentTrack.value?.previewOffset || 0)

// ── Preview window on the waveform ────────────────────────────────
// The waveform shows the WHOLE track on purpose — a buyer should see what
// they are buying — but only the preview window is playable. So progress and
// seeking are measured against the full duration, and the canvas marks the
// window rather than pretending the track is only that long.
//
// `duration` is not usable for this: it is deliberately overwritten with
// previewLength so the time labels count the preview, not the file.
const audioFullDuration = ref(0)
const fullDuration = computed(() => {
    // With preview bounds the <audio> element holds only the SLICE, so its
    // duration is the window — never the scale for a waveform drawn from the
    // whole song. It used to be the fallback here, and since no shop page
    // passed `duration` it was ALWAYS the value that got used: the window is
    // the middle 50%, so the playhead started at 50% of the waveform and ran
    // off the right edge at 150%, with the highlighted region doing the same.
    //
    // Without a real track duration there is nothing honest to map against, so
    // the waveform is treated as the window itself: the playhead then runs 0→1
    // across it. A different picture from the intended one, but a coherent one.
    if (hasPreviewBounds.value) {
        return Number(currentTrack.value?.duration) || 0
    }
    return audioFullDuration.value || Number(currentTrack.value?.duration) || 0
})

const waveProgress = computed(() => {
    if (!hasPreviewBounds.value) return duration.value ? currentTime.value / duration.value : 0
    // No full duration known — fall back to spanning the window (see above).
    if (!fullDuration.value) return previewLength.value ? currentTime.value / previewLength.value : 0
    return (previewOffset.value + currentTime.value) / fullDuration.value
})
const previewRegionStart = computed(() =>
    hasPreviewBounds.value && fullDuration.value ? (previewOffset.value + previewStart.value) / fullDuration.value : 0)
const previewRegionEnd = computed(() =>
    hasPreviewBounds.value && fullDuration.value ? (previewOffset.value + previewEnd.value) / fullDuration.value : 1)


// ── Cover-driven colour ────────────────────────────────────────
// Sample the dominant colour of the current artwork and feed it into
// the visualizer + smoke. We weight saturated pixels higher than greys
// so a cover with a dark navy backdrop and a vivid pink subject ends
// up tinting the player pink, not a muddy grey. CORS-tainted reads
// (S3 buckets without an ACAO header) silently fall back to the tenant
// primary — the player just stays on its existing palette.
function hexOrRgbToChannels(input) {
  if (!input) return '0 242 255'
  const s = String(input).trim()
  if (s.startsWith('#')) {
    const hex = s.slice(1)
    const full = hex.length === 3
      ? hex.split('').map(c => c + c).join('')
      : hex.padEnd(6, '0').slice(0, 6)
    const n = parseInt(full, 16)
    return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`
  }
  const m = s.match(/rgba?\(([^)]+)\)/i)
  if (m) {
    const parts = m[1].split(',').slice(0, 3).map(x => Math.round(parseFloat(x)))
    if (parts.length === 3 && parts.every(n => Number.isFinite(n))) {
      return `${parts[0]} ${parts[1]} ${parts[2]}`
    }
  }
  return '0 242 255'
}

// Blend a fixed reference colour (e.g. the purple / pink stops baked
// into the gradient) toward the cover-derived colour. `weight` is the
// share of the cover colour in the result — 0 keeps the reference,
// 1 returns the cover colour. Used by the stroke gradient so the
// interior stops follow the cover instead of staying purple/pink for
// every track.
function mixWithCover(refHex, weight) {
  const coverChans = primaryChannels.value.split(/\s+/).map(n => parseInt(n, 10))
  const refChans = hexOrRgbToChannels(refHex).split(/\s+/).map(n => parseInt(n, 10))
  if (refChans.length !== 3 || coverChans.length !== 3) return refHex
  const r = Math.round(refChans[0] * (1 - weight) + coverChans[0] * weight)
  const g = Math.round(refChans[1] * (1 - weight) + coverChans[1] * weight)
  const b = Math.round(refChans[2] * (1 - weight) + coverChans[2] * weight)
  return `rgb(${r}, ${g}, ${b})`
}

// Return a shade of the cover-derived colour with the hue rotated and
// lightness adjusted by the supplied deltas. Used to derive the line's
// interior gradient stops from the cover itself, so the whole gradient
// reads as variations of one hue family instead of a fixed purple/pink
// transit ignoring the artwork.
// Brighten a colour toward white. `factor` is the share of white in
// the result (0 = unchanged, 1 = pure white). Used ONLY for the
// visualizer paint — the sampler's ×0.75 dim still applies to the
// player chrome (background ring, halo, smoke) so brightening the
// line doesn't undo the "less bright player" pass.
function brightenColor(rgbStr, factor) {
  const chans = hexOrRgbToChannels(rgbStr).split(/\s+/).map(n => parseInt(n, 10))
  if (chans.length !== 3) return rgbStr
  const k = Math.max(0, Math.min(1, factor))
  const r = Math.round(chans[0] + (255 - chans[0]) * k)
  const g = Math.round(chans[1] + (255 - chans[1]) * k)
  const b = Math.round(chans[2] + (255 - chans[2]) * k)
  return `rgb(${r}, ${g}, ${b})`
}

function shadeOfCover(hueShiftDeg, lightnessDelta) {
  const chans = primaryChannels.value.split(/\s+/).map(n => parseInt(n, 10))
  if (chans.length !== 3) return primaryColor.value
  const [r, g, b] = chans.map(n => n / 255)
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0, s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break
      case g: h = (b - r) / d + 2; break
      default: h = (r - g) / d + 4
    }
    h *= 60
  }
  h = (h + hueShiftDeg + 360) % 360
  const nl = Math.max(0, Math.min(1, l + lightnessDelta))
  const hueToRgb = (p, q, t) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  let nr, ng, nb
  if (s === 0) { nr = ng = nb = nl }
  else {
    const q = nl < 0.5 ? nl * (1 + s) : nl + s - nl * s
    const p = 2 * nl - q
    const hh = h / 360
    nr = hueToRgb(p, q, hh + 1 / 3)
    ng = hueToRgb(p, q, hh)
    nb = hueToRgb(p, q, hh - 1 / 3)
  }
  return `rgb(${Math.round(nr * 255)}, ${Math.round(ng * 255)}, ${Math.round(nb * 255)})`
}

let dominantSampleSeq = 0
async function sampleDominantColor(url) {
  if (!url) {
    dominantColor.value = null
    return
  }
  const mySeq = ++dominantSampleSeq
  try {
    const colour = await new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.referrerPolicy = 'no-referrer'
      img.onload = () => {
        try {
          const size = 16
          const cv = document.createElement('canvas')
          cv.width = size
          cv.height = size
          const ctx = cv.getContext('2d', { willReadFrequently: true })
          if (!ctx) return reject(new Error('no-2d'))
          ctx.drawImage(img, 0, 0, size, size)
          const data = ctx.getImageData(0, 0, size, size).data
          let r = 0, g = 0, b = 0, total = 0
          for (let i = 0; i < data.length; i += 4) {
            const cr = data[i], cg = data[i + 1], cb = data[i + 2], ca = data[i + 3]
            if (ca < 200) continue
            const max = Math.max(cr, cg, cb)
            const min = Math.min(cr, cg, cb)
            // Saturation 0..1 — vivid pixels weigh more so the cover's
            // visible accent wins over a neutral background.
            const sat = max === 0 ? 0 : (max - min) / max
            const w = 0.25 + sat * 1.6
            r += cr * w
            g += cg * w
            b += cb * w
            total += w
          }
          if (total === 0) return reject(new Error('empty'))
          let mr = Math.round(r / total)
          let mg = Math.round(g / total)
          let mb = Math.round(b / total)
          // Two-step brightness cap:
          //   1. Lift very dark averages to a softer floor (0.22 luma)
          //      so a black-heavy cover still produces a visible accent
          //      without the previous near-white bias.
          //   2. Dim the result by 25 % across the board so the haze /
          //      ring / glow read as a saturated tint, not a headlight.
          //      User explicitly asked for less brightness here.
          const luma = (0.299 * mr + 0.587 * mg + 0.114 * mb) / 255
          if (luma < 0.22) {
            const k = 0.22 / Math.max(0.06, luma)
            mr = Math.min(255, Math.round(mr * k))
            mg = Math.min(255, Math.round(mg * k))
            mb = Math.min(255, Math.round(mb * k))
          }
          const DIM = 0.75
          mr = Math.round(mr * DIM)
          mg = Math.round(mg * DIM)
          mb = Math.round(mb * DIM)
          resolve(`rgb(${mr}, ${mg}, ${mb})`)
        } catch (err) { reject(err) }
      }
      img.onerror = (err) => reject(err)
      img.src = url
    })
    if (mySeq === dominantSampleSeq) dominantColor.value = colour
  } catch {
    if (mySeq === dominantSampleSeq) dominantColor.value = null
  }
}

// Client-only: sampleDominantColor touches `document` / `Image`, which
// would throw on SSR. The function itself early-returns on empty url
// so the `immediate: true` first tick is safe today (SSR has no
// currentTrack and so no artworkSrc), but the import.meta.client gate
// keeps it that way if a future change ever lets a track persist into
// the SSR render path.
if (import.meta.client) {
  watch(artworkSrc, (url) => sampleDominantColor(url), { immediate: true })
}

const loadAndPlay = async () => {
  if (!audioElement.value || !currentTrack.value) return

  audioElement.value.pause()
  audioElement.value.currentTime = 0

  const url = currentTrack.value.type === 'suno'
    ? currentTrack.value.url
    : getProxiedUrl(currentTrack.value.url)
  audioElement.value.src = url
  audioElement.value.load()

  loadPeaks()

  if (hasPreviewBounds.value) {
    audioElement.value.currentTime = previewStart.value
    currentTime.value = previewStart.value
  }

  if (isPlaying.value) {
    try {
      await audioElement.value.play()
    } catch (e) {
      // AbortError fires when our play() gets superseded by another
      // play()/load() in the same tick — that's the isPlaying watcher
      // also calling play(). Benign: the second call wins and the
      // audio still plays. Only treat NON-abort errors as real failures.
      if (e?.name !== 'AbortError') {
        console.error('Playback failed', e)
        isPlaying.value = false
      }
    }
  }
}

// Monotonic counter so a stale waveform fetch can't clobber a newer one.
let peaksRequestSeq = 0

const loadPeaks = async () => {
  isLoadingPeaks.value = true
  peaksData.value = []
  const mySeq = ++peaksRequestSeq

  const track = currentTrack.value
  if (!track) {
    isLoadingPeaks.value = false
    return
  }

  let rawPeaks = []

  if (track.audio && Array.isArray(track.audio.waveform) && track.audio.waveform.length > 0) {
    rawPeaks = track.audio.waveform
  } else {
    let peaksUrl = track.peaksUrl
    if (!peaksUrl && track.audio?.peaks?.url) peaksUrl = track.audio.peaks.url

    if (peaksUrl) {
      const url = getProxiedUrl(peaksUrl)
      try {
        const res = await fetch(url)
        if (res.ok) {
          const json = await res.json()
          rawPeaks = json.data || []
        }
      } catch (e) { console.error('Peaks load error', e) }
    } else if (track.id && !String(track.id).startsWith('suno_') && track.type !== 'suno') {
      try {
        const res = await $fetch(`/api/shop/tracks/${encodeURIComponent(track.id)}/waveform`)
        if (Array.isArray(res?.waveform)) rawPeaks = res.waveform
      } catch (e) {
        // 404 / non-shop — fall through to the fake waveform.
      }
    }
  }

  if (mySeq !== peaksRequestSeq) return

  if (rawPeaks.length > 0) {
    peaksData.value = normalizeWaveform(rawPeaks)
  }
  if (peaksData.value.length === 0) {
    peaksData.value = generateFakeWaveform(140)
  }
  isLoadingPeaks.value = false
}

onMounted(() => {
  // Construct the Audio element on the client only — guards against any
  // accidental SSR render of this teleported component.
  audioElement.value = new Audio()
  audioElement.value.preload = 'auto'
  audioElement.value.volume = volume.value

  // Resolve the tenant's --primary token once for the waveform canvas.
  // CSS custom properties don't work as canvas fillStyle, so we have to
  // read the computed value into a normal color string.
  const tenantPrimary = getComputedStyle(document.documentElement)
    .getPropertyValue('--primary').trim()
  if (tenantPrimary) tenantPrimaryHex.value = tenantPrimary

  // Visualizer is purely decorative — skip the whole rAF loop for users
  // who opted into reduced motion.
  vizMotionAllowed = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  window.addEventListener('resize', onVizResize)

  // If a track was already in the shared composable when this component
  // mounted (e.g. the user navigated /shop → /admin → /shop), re-prime
  // the audio element so play/pause works without requiring the user to
  // re-click a track.
  if (currentTrack.value) {
    loadAndPlay()
  }

  const audio = audioElement.value

  audio.ontimeupdate = () => {
    currentTime.value = audio.currentTime
    if (hasPreviewBounds.value && audio.currentTime >= previewEnd.value) {
      if (looping.value) {
        audio.currentTime = previewStart.value
        currentTime.value = previewStart.value
      } else {
        audio.pause()
        isPlaying.value = false
        audio.currentTime = previewStart.value
        currentTime.value = previewStart.value
      }
    }
    maybeSendHeartbeat()
  }

  audio.onloadedmetadata = () => {
    audioFullDuration.value = audio.duration || 0
    if (hasPreviewBounds.value) {
      duration.value = previewLength.value
    } else {
      duration.value = audio.duration
    }
  }

  audio.onended = () => {
    if (looping.value) {
      audio.currentTime = 0
      currentTime.value = 0
      audio.play().catch(() => {})
      return
    }
    // Auto-advance: pop the next track off the queue if there is one.
    // `advanceQueue` no-ops + returns false on an empty queue, so the
    // existing "stop on end" behaviour is preserved when nothing is
    // lined up.
    const advanced = player.advanceQueue?.()
    if (!advanced) isPlaying.value = false
  }

  audio.onerror = (e) => {
    console.error('Audio Error', e)
    isPlaying.value = false
  }
})

const handleSeek = (percent) => {
  if (!audioElement.value) return
  if (hasPreviewBounds.value) {
    // The click lands on a FULL-track waveform, so it maps to the full
    // duration and is then clamped into the preview. Mapping it onto the
    // preview length instead (as this did) meant a click at 90% of a visibly
    // full waveform jumped to 90% of the preview — the playhead never went
    // where the pointer was.
    const total = fullDuration.value || audioElement.value.duration || 0
    const target = percent * total - previewOffset.value
    const clamped = Math.min(Math.max(target, previewStart.value), previewEnd.value)
    audioElement.value.currentTime = clamped
    currentTime.value = clamped
  } else {
    const newTime = percent * (audioElement.value.duration || 0)
    audioElement.value.currentTime = newTime
    currentTime.value = newTime
  }
}

// ── Play telemetry (mirrors GlobalAudioPlayer) ───────────────────
const playSession = ref({
  trackId: null,
  sessionKey: null,
  startedAtMs: 0,
  playedMs: 0,
  lastTickAtMs: 0,
  heartbeatState: 'idle',
  backoffUntilMs: 0
})
const PLAY_HEARTBEAT_THRESHOLD_MS = 30 * 1000
const PLAY_HEARTBEAT_BACKOFF_MS = 30 * 1000

function resetPlaySession() {
  playSession.value = {
    trackId: null,
    sessionKey: null,
    startedAtMs: 0,
    playedMs: 0,
    lastTickAtMs: 0,
    heartbeatState: 'idle',
    backoffUntilMs: 0
  }
}

async function startPlaySession(trackId) {
  if (!trackId || String(trackId).startsWith('suno_')) {
    resetPlaySession()
    return
  }
  if (currentTrack.value?.type === 'suno') {
    resetPlaySession()
    return
  }
  try {
    const res = await $fetch(`/api/feed/tracks/${trackId}/play/start`, {
      method: 'POST',
      body: { source: 'shop' }
    })
    playSession.value = {
      trackId: String(trackId),
      sessionKey: res?.sessionKey || null,
      startedAtMs: Date.now(),
      playedMs: 0,
      lastTickAtMs: Date.now(),
      heartbeatState: 'idle',
      backoffUntilMs: 0
    }
  } catch (err) {
    resetPlaySession()
  }
}

async function maybeSendHeartbeat() {
  const s = playSession.value
  if (!s.sessionKey) return
  if (s.heartbeatState === 'sent' || s.heartbeatState === 'sending') return
  if (s.heartbeatState === 'failed' && Date.now() < s.backoffUntilMs) return

  const now = Date.now()
  if (isPlaying.value && s.lastTickAtMs > 0) {
    const delta = now - s.lastTickAtMs
    if (delta > 0 && delta < 1500) {
      s.playedMs += delta
    }
  }
  s.lastTickAtMs = now

  if (s.playedMs < PLAY_HEARTBEAT_THRESHOLD_MS) return

  s.heartbeatState = 'sending'
  try {
    await $fetch(`/api/feed/tracks/${s.trackId}/play/heartbeat`, {
      method: 'POST',
      body: {
        sessionKey: s.sessionKey,
        secondsPlayed: Math.floor(s.playedMs / 1000)
      }
    })
    s.heartbeatState = 'sent'
  } catch {
    s.heartbeatState = 'failed'
    s.backoffUntilMs = Date.now() + PLAY_HEARTBEAT_BACKOFF_MS
  }
}

watch(currentTrack, (newTrack, oldTrack) => {
  if (newTrack) {
    if (!oldTrack || newTrack.id !== oldTrack.id) {
      loadAndPlay()
      startPlaySession(newTrack.id)
    }
  } else {
    if (audioElement.value) {
      audioElement.value.pause()
      audioElement.value.src = ''
    }
    peaksData.value = []
    resetPlaySession()
    // Track cleared (player closed) — tear the visualizer down too
    // so its canvas unmounts behind the slide-out.
    stopViz()
  }
})

watch(isPlaying, (playing) => {
  if (!audioElement.value || !audioElement.value.src) return
  if (playing) {
    audioElement.value.play().catch(e => {
      if (e?.name !== 'AbortError') console.error('Play error', e)
    })
    startViz()
  } else {
    audioElement.value.pause()
    stopViz()
  }
})

watch(volume, (vol) => {
  if (audioElement.value) audioElement.value.volume = vol
})

watch(currentTime, (newTime) => {
  if (!audioElement.value) return
  if (Math.abs(audioElement.value.currentTime - newTime) > 0.5) {
    audioElement.value.currentTime = newTime
  }
})

// ── Visualizer (peaks-driven, no Web Audio) ────────────────────
// The shape comes from the current track's `peaksData` (already
// loaded by loadPeaks). We window a slice of peaks centered on the
// current playback position and remap it across the canvas width —
// loud sections of the track make the line shoot up exactly where
// they are in real time. A subtle ambient pulse + extra phase noise
// keeps the curve alive between actual peaks so paused/quiet moments
// still feel breathing instead of flat.

function resizeViz() {
  const c = vizCanvasRef.value
  if (!c) return
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const rect = c.getBoundingClientRect()
  c.width = Math.max(1, Math.floor(rect.width * dpr))
  c.height = Math.max(1, Math.floor(rect.height * dpr))
  const ctx = c.getContext('2d')
  ctx && ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
}

function drawViz() {
  const c = vizCanvasRef.value
  if (!c) {
    vizRafId = null
    return
  }
  const ctx = c.getContext('2d')
  if (!ctx) {
    vizRafId = null
    return
  }
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const w = c.width / dpr
  const h = c.height / dpr

  const peaks = peaksData.value
  if (!peaks || peaks.length === 0) {
    // No peaks loaded yet — wait. Don't paint anything; the canvas
    // stays cleared from the last frame.
    vizRafId = requestAnimationFrame(drawViz)
    return
  }

  ctx.clearRect(0, 0, w, h)

  const numPoints = 96
  if (!vizSmoothed || vizSmoothed.length !== numPoints) {
    vizSmoothed = new Float32Array(numPoints)
  }

  // Window of peaks centered on the current playback position. We span
  // ±12% of the track around `currentTime` so the line shows the local
  // shape of the track at the playhead — past on the left, future on
  // the right. As playback advances, this slice scrolls through the
  // full waveform.
  // Windowed against the FULL track, because `peaksData` holds the full
  // track's peaks. Measuring progress against previewLength here (as it did)
  // scrolled the line through the entire waveform during a 30s preview —
  // the same mix-up the progress bar had, just less obvious because the line
  // is decorative.
  const dur = hasPreviewBounds.value ? fullDuration.value : (duration.value || 0)
  const progress = Math.min(1, Math.max(0, waveProgress.value))
  const windowHalf = 0.12   // ±12% of the track
  const startT = Math.max(0, progress - windowHalf)
  const endT   = Math.min(1, progress + windowHalf)
  // Avoid a zero-width window when duration is unknown — fall back to
  // showing the entire peaks array so the line still draws something.
  const usableStart = dur > 0 ? startT : 0
  const usableEnd   = dur > 0 ? endT   : 1

  // Add a soft sine ambient so the line breathes even on silence and
  // a hash-like noise so adjacent frames don't paint identical curves.
  const tNow = performance.now() / 1000
  const breathing = isPlaying.value ? 0.06 : 0.02
  const ambient = (Math.sin(tNow * 1.6) * 0.5 + 0.5) * breathing
  const smoothCoeff = isPlaying.value ? 0.22 : 0.08

  for (let i = 0; i < numPoints; i++) {
    const tRel = i / (numPoints - 1)
    const peakT = usableStart + tRel * (usableEnd - usableStart)
    const idx = Math.min(peaks.length - 1, Math.floor(peakT * peaks.length))
    const raw = Math.abs(peaks[idx] || 0)
    // Per-point noise wobble so neighbouring frames differ slightly.
    const noise = (Math.sin(tNow * 7 + i * 0.9) * 0.5 + 0.5) * 0.06
    const target = Math.min(1, raw + ambient + noise)
    vizSmoothed[i] += (target - vizSmoothed[i]) * smoothCoeff
  }

  // Bend the line into a Torbogen — a sharpened half-sine arch as the
  // baseline. Raising sin(πt) to a power < 1 keeps the same 0→1→0
  // envelope but makes the flanks drop more steeply at the edges, so
  // the ends curl down harder while the middle stays high. Amplitude
  // peaks then push *further* up from that arch.
  const archAmount = h * 0.55       // ~83px on a 150px canvas
  const ampScale   = h * 0.42       // peaks add up to ~63px on top
  const pts = new Array(numPoints)
  for (let i = 0; i < numPoints; i++) {
    const tRel = i / (numPoints - 1)
    const v = Math.pow(vizSmoothed[i], 1.8)
    // 0.55 exponent → noticeably steeper falloff than pure sin (≈ 1).
    const archBase = Math.pow(Math.sin(tRel * Math.PI), 0.55) * archAmount
    const ampHeight = v * ampScale
    pts[i] = { x: tRel * w, y: h - archBase - ampHeight }
  }

  // Fill under the line — gradient that fades to nothing at the top.
  // Tinted with the cover-derived channels so the wash matches whatever
  // accent currently lives in `primaryChannels`. Falls back gracefully
  // to the tenant primary when the sampler hasn't produced a colour
  // for this track yet (e.g. CORS-tainted artwork).
  // Use a brightened channel set for the under-line fill too, so the
  // wash beneath the curve reads as a lit gradient rather than a dim
  // shade. The brightener takes the cover colour and pushes ~38 % toward
  // white before splitting back into "r, g, b" for rgba() consumption.
  const brightFillCh = (() => {
    const lit = brightenColor(`rgb(${primaryChannels.value.split(/\s+/).join(', ')})`, 0.38)
    return hexOrRgbToChannels(lit).split(/\s+/).join(', ')
  })()
  const fill = ctx.createLinearGradient(0, 0, 0, h)
  fill.addColorStop(0,    `rgba(${brightFillCh}, 0)`)
  fill.addColorStop(0.55, `rgba(${brightFillCh}, 0.22)`)
  fill.addColorStop(1,    `rgba(${brightFillCh}, 0.48)`)

  ctx.beginPath()
  ctx.moveTo(0, h)
  ctx.lineTo(pts[0].x, pts[0].y)
  for (let i = 1; i < pts.length - 1; i++) {
    const xc = (pts[i].x + pts[i + 1].x) / 2
    const yc = (pts[i].y + pts[i + 1].y) / 2
    ctx.quadraticCurveTo(pts[i].x, pts[i].y, xc, yc)
  }
  ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y)
  ctx.lineTo(w, h)
  ctx.closePath()
  ctx.fillStyle = fill
  ctx.fill()

  // Stroke — horizontal gradient built from the cover-derived colour,
  // BRIGHTENED for the visualizer paint only. The sampler dims the
  // raw cover colour ×0.75 so the player chrome stays calm; the line
  // is meant to be the headline element, so we push each stop ~40 %
  // toward white and add a hot core stop in the middle. Painted in
  // three passes (wide outer halo → main stroke → thin bright core)
  // so the line reads as glowing rather than flat.
  const baseStart = brightenColor(primaryColor.value, 0.42)
  const baseEnd   = brightenColor(primaryColor.value, 0.42)
  const baseMidA  = brightenColor(shadeOfCover(-18, 0.12), 0.32)
  const baseMidB  = brightenColor(shadeOfCover(18, -0.10), 0.32)
  const hotCore   = brightenColor(primaryColor.value, 0.78)
  const stroke = ctx.createLinearGradient(0, 0, w, 0)
  stroke.addColorStop(0,    baseStart)
  stroke.addColorStop(0.3,  baseMidA)
  stroke.addColorStop(0.5,  hotCore)
  stroke.addColorStop(0.7,  baseMidB)
  stroke.addColorStop(1,    baseEnd)

  const drawPath = () => {
    ctx.beginPath()
    ctx.moveTo(pts[0].x, pts[0].y)
    for (let i = 1; i < pts.length - 1; i++) {
      const xc = (pts[i].x + pts[i + 1].x) / 2
      const yc = (pts[i].y + pts[i + 1].y) / 2
      ctx.quadraticCurveTo(pts[i].x, pts[i].y, xc, yc)
    }
    ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y)
  }

  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'

  // Pass 1 — wide diffuse halo. Low alpha + large shadow blur. Gives
  // the line a soft glow that bleeds well past its own width.
  ctx.globalAlpha = 0.55
  ctx.lineWidth = 6
  ctx.shadowBlur = 36
  ctx.shadowColor = brightenColor(primaryColor.value, 0.5)
  ctx.strokeStyle = stroke
  drawPath()
  ctx.stroke()

  // Pass 2 — main stroke. Full alpha + medium shadow. This is what
  // reads as "the line" — wider than before (2 → 2.6 px) so the body
  // of the curve looks bolder.
  ctx.globalAlpha = 1
  ctx.lineWidth = 2.6
  ctx.shadowBlur = 22
  ctx.shadowColor = brightenColor(primaryColor.value, 0.6)
  drawPath()
  ctx.stroke()

  // Pass 3 — hot inner core. A thin near-white stroke painted on top
  // sells the "lit from within" effect — the colour stays cover-keyed
  // but the very centre of the line burns bright.
  ctx.globalAlpha = 0.9
  ctx.lineWidth = 1
  ctx.shadowBlur = 0
  ctx.strokeStyle = brightenColor(primaryColor.value, 0.75)
  drawPath()
  ctx.stroke()

  // Reset alpha for the next frame's fill pass.
  ctx.globalAlpha = 1

  vizRafId = requestAnimationFrame(drawViz)
}

async function startViz() {
  if (!vizMotionAllowed) return
  vizActive.value = true
  await nextTick()
  resizeViz()
  if (vizRafId == null) vizRafId = requestAnimationFrame(drawViz)
}

function stopViz() {
  if (vizRafId != null) {
    cancelAnimationFrame(vizRafId)
    vizRafId = null
  }
  // Keep the canvas mounted briefly so the fade-out reads — but
  // wipe its content so a stale last-frame curve doesn't ghost
  // into the next play.
  const c = vizCanvasRef.value
  if (c) {
    const ctx = c.getContext('2d')
    ctx && ctx.clearRect(0, 0, c.width, c.height)
  }
  vizActive.value = false
}

const onVizResize = () => resizeViz()

onBeforeUnmount(() => {
  stopViz()
  vizSmoothed = null
  window.removeEventListener('resize', onVizResize)
  if (audioElement.value) {
    audioElement.value.pause()
    audioElement.value.src = ''
    audioElement.value = null
  }
})
</script>

<style scoped>
/* ── Shell: full-bleed positioning host that centers a max-width card ─ */
.shop-player-shell {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  justify-content: center;
  padding: 0 24px 18px;
  pointer-events: none;
  /* Reserve no space until a track loads — the shell stays mounted so we
     get a smooth slide-in transition. */
}

/* ── Visualizer: gradient line that shoots up from behind the player. ── */
.player-viz {
  position: fixed;
  /* Confined to the player's horizontal extent — same max-width and
     gutters as the floating card so the line lives ONLY in the bar's
     footprint, not across the full viewport. */
  left: 50%;
  /* Canvas bottom sits BEHIND the top of the player frame — player
     covers viewport-bottom 18px → 94px (76px tall). Lowering the
     baseline from 74px → 60px deepens the overlap (≈34px), pulling
     the arch a touch further into the bar so less of the curve floats
     above the player. The wave's baseline (always at canvas bottom in
     drawViz) stays hidden behind the player either way. */
  bottom: 60px;
  transform: translateX(-50%);
  /* 10% narrower than the player's full content width on every
     viewport: player uses (100% - 48px) with a 1532px cap, so viz
     uses 90% of that with a matching cap (1532 * 0.9 ≈ 1379px). */
  width: calc((100% - 48px) * 0.9);
  max-width: 1379px;
  /* Shortened 15 % (150 → 128 px). The internal arch math is height-
     relative (archAmount = h*0.55, ampScale = h*0.42 inside drawViz),
     so reducing the canvas height also lowers the visible peaks
     proportionally — keeping the curve's shape consistent at the new
     size without retuning the painter. */
  height: 128px;
  /* z-index ONE BELOW the player so the line is partially eaten by the
     player's bottom edge — exactly the "shooting out from under" look
     the user asked for. */
  z-index: 9998;
  pointer-events: none;
  /* Two mask layers, composited with `intersect` — the visible area is
     where BOTH are opaque. The vertical layer lets the top of the arch
     dissolve into the page bg; the horizontal layer fades the line
     into transparency at the left + right ends so the arch reads as a
     standalone shape rather than a clipped strip. */
  -webkit-mask-image:
    linear-gradient(to top, #000 88%, transparent 100%),
    linear-gradient(to right, transparent 0%, #000 32%, #000 68%, transparent 100%);
  -webkit-mask-composite: source-in;
          mask-image:
    linear-gradient(to top, #000 88%, transparent 100%),
    linear-gradient(to right, transparent 0%, #000 32%, #000 68%, transparent 100%);
          mask-composite: intersect;
  will-change: opacity;
}

.viz-fade-enter-active {
  transition: opacity 0.55s ease;
}
.viz-fade-leave-active {
  transition: opacity 0.4s ease;
}
.viz-fade-enter-from,
.viz-fade-leave-to {
  opacity: 0;
}

/* ── Smoke: slow radial fog drifting outward from the player centre. ─
   Three radial-gradient blobs share the same origin and drift in
   different directions at slightly different speeds so the cloud
   never resets to a flat shape — it keeps churning slowly. Colour
   pulls from --player-accent-channels (the cover-derived RGB tuple)
   so the haze follows whatever the visualizer is currently showing. */
.player-smoke {
  position: fixed;
  /* Full-bleed via left/right: 0 instead of width: 100vw — `100vw`
     includes the scrollbar width and would trigger a horizontal
     scrollbar on any page with a vertical scrollbar. Stretching with
     left/right uses the viewport content width (scrollbar-exclusive)
     and centres naturally without a transform. */
  left: 0;
  right: 0;
  /* Dropped further (−12px → −32px) so the haze is anchored well
     below the player's bottom edge. The dense centre is now mostly
     under the viewport baseline; what reads on-screen is just the
     upper edge of the cloud trailing out to the sides. */
  bottom: -32px;
  height: 260px;
  /* Sits BELOW the visualizer (9998) so the gradient line paints on
     top of the haze. Player shell at 9999 still wins above both. */
  z-index: 9996;
  pointer-events: none;
  overflow: visible;
  filter: blur(2px);
  /* Reduced from 0.85 → 0.55. The haze is a backdrop tint now, not a
     headline element — the line and player chrome carry the focus. */
  opacity: 0.55;
  -webkit-mask-image: radial-gradient(150% 100% at 50% 100%, #000 0%, #000 60%, transparent 100%);
          mask-image: radial-gradient(150% 100% at 50% 100%, #000 0%, #000 60%, transparent 100%);
}
.smoke-blob {
  position: absolute;
  left: 50%;
  top: 50%;
  /* Stretched horizontally + flattened vertically so each blob reads
     as a long sideways drift rather than a circular puff under the
     player. Width well past 100% so neighbouring blobs overlap and
     blend across the viewport's full span. */
  width: 140%;
  height: 200%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  /* Mix-blend lets the cover colour interact with whatever sits
     behind the bar (page bg, hero gradient) instead of stamping a
     flat tint on top. */
  mix-blend-mode: screen;
  will-change: transform, opacity;
}
/* Smoke is mostly off-white with a faint cover-colour wash on top — a
   single accent-only gradient read as "glow", not "smoke". The white
   base now carries the body of the haze and the coloured layer is just
   a hint that recolours subtly with the artwork. Layering inside one
   `background` shorthand: the white radial sits underneath, the
   accent radial sits on top with much lower alpha. */
.smoke-blob-1 {
  background:
    radial-gradient(
      ellipse 60% 50% at 50% 50%,
      rgb(var(--player-accent-channels, 0 242 255) / 0.08) 0%,
      rgb(var(--player-accent-channels, 0 242 255) / 0.03) 50%,
      transparent 85%
    ),
    radial-gradient(
      ellipse 60% 50% at 50% 50%,
      rgba(230, 230, 235, 0.28) 0%,
      rgba(230, 230, 235, 0.12) 45%,
      transparent 80%
    );
  /* Spreads from centre outward to the LEFT. Two blob instances are
     handled by `animation-delay` on the duplicated keyframe at half
     the cycle (negative delay) so a fresh puff is always re-emerging
     from the middle as the previous one fades into the edge. */
  animation: smoke-spread-left 17s cubic-bezier(0.35, 0.05, 0.6, 0.95) infinite;
}
.smoke-blob-2 {
  background:
    radial-gradient(
      ellipse 70% 45% at 50% 50%,
      rgb(var(--player-accent-channels, 0 242 255) / 0.06) 0%,
      rgb(var(--player-accent-channels, 0 242 255) / 0.02) 55%,
      transparent 88%
    ),
    radial-gradient(
      ellipse 70% 45% at 50% 50%,
      rgba(230, 230, 235, 0.22) 0%,
      rgba(230, 230, 235, 0.09) 50%,
      transparent 85%
    );
  /* Mirror of blob-1 — same cycle, opposite direction, no delay so
     left + right emit in lockstep from the player centre. */
  animation: smoke-spread-right 23s cubic-bezier(0.42, 0.0, 0.55, 0.9) infinite;
  animation-delay: -4s;
}
.smoke-blob-3 {
  background:
    radial-gradient(
      ellipse 80% 40% at 50% 50%,
      rgb(var(--player-accent-channels, 0 242 255) / 0.05) 0%,
      rgb(var(--player-accent-channels, 0 242 255) / 0.02) 60%,
      transparent 92%
    ),
    radial-gradient(
      ellipse 80% 40% at 50% 50%,
      rgba(230, 230, 235, 0.18) 0%,
      rgba(230, 230, 235, 0.07) 55%,
      transparent 90%
    );
  /* Centre pulse — the seed that the outward spreads emerge from. A
     slow scale-only loop keeps the middle dense between puffs so the
     spread doesn't visibly "blink off" at the moment one cycle ends
     and the next begins. */
  animation: smoke-pulse-center 19s cubic-bezier(0.45, 0.0, 0.55, 1) infinite;
}

/* Second pair, offset by their own cycles. Each gets its OWN keyframe
   so the two left blobs (1, 4) don't trace identical paths and the
   right pair (2, 5) doesn't either — irregular durations + slightly
   different scale + Y-drift values mean the puffs never line up the
   same way twice. */
.smoke-blob-4,
.smoke-blob-5 {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 140%;
  height: 200%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  mix-blend-mode: screen;
  will-change: transform, opacity;
}
.smoke-blob-4 {
  background:
    radial-gradient(
      ellipse 55% 60% at 50% 50%,
      rgb(var(--player-accent-channels, 0 242 255) / 0.06) 0%,
      rgb(var(--player-accent-channels, 0 242 255) / 0.02) 50%,
      transparent 85%
    ),
    radial-gradient(
      ellipse 55% 60% at 50% 50%,
      rgba(230, 230, 235, 0.2) 0%,
      rgba(230, 230, 235, 0.08) 45%,
      transparent 80%
    );
  animation: smoke-spread-left-b 25s ease-in-out infinite;
  animation-delay: -7s;
}
.smoke-blob-5 {
  background:
    radial-gradient(
      ellipse 80% 38% at 50% 50%,
      rgb(var(--player-accent-channels, 0 242 255) / 0.05) 0%,
      rgb(var(--player-accent-channels, 0 242 255) / 0.02) 55%,
      transparent 88%
    ),
    radial-gradient(
      ellipse 80% 38% at 50% 50%,
      rgba(230, 230, 235, 0.16) 0%,
      rgba(230, 230, 235, 0.06) 50%,
      transparent 85%
    );
  animation: smoke-spread-right-b 21s ease-in-out infinite;
  animation-delay: -13s;
}

/* Five distinct keyframes — different durations, different easings,
   different vertical wobble and scale targets per blob — so no two
   puffs ever trace the same path. Smoke needs that irregularity to
   read as smoke instead of a metronome. */
@keyframes smoke-spread-left {
  0%   { transform: translate(-50%, -50%) scale(0.55) translate(  0%,  0%); opacity: 0; }
  18%  { transform: translate(-50%, -50%) scale(0.8)  translate(-12%, -2%); opacity: 0.5; }
  55%  { transform: translate(-50%, -50%) scale(1.05) translate(-42%, -6%); opacity: 0.55; }
  82%  { transform: translate(-50%, -50%) scale(1.2)  translate(-62%, -3%); opacity: 0.3; }
  100% { transform: translate(-50%, -50%) scale(1.3)  translate(-78%, -1%); opacity: 0; }
}
@keyframes smoke-spread-left-b {
  0%   { transform: translate(-50%, -50%) scale(0.7)  translate(  2%,  1%); opacity: 0; }
  25%  { transform: translate(-50%, -50%) scale(0.95) translate(-22%,  3%); opacity: 0.45; }
  62%  { transform: translate(-50%, -50%) scale(1.15) translate(-55%,  6%); opacity: 0.5; }
  100% { transform: translate(-50%, -50%) scale(1.35) translate(-82%,  9%); opacity: 0; }
}
@keyframes smoke-spread-right {
  0%   { transform: translate(-50%, -50%) scale(0.62) translate( -1%, -1%); opacity: 0; }
  22%  { transform: translate(-50%, -50%) scale(0.9)  translate( 18%,  2%); opacity: 0.5; }
  58%  { transform: translate(-50%, -50%) scale(1.08) translate( 48%, -4%); opacity: 0.55; }
  88%  { transform: translate(-50%, -50%) scale(1.22) translate( 68%, -2%); opacity: 0.25; }
  100% { transform: translate(-50%, -50%) scale(1.3)  translate( 76%,  1%); opacity: 0; }
}
@keyframes smoke-spread-right-b {
  0%   { transform: translate(-50%, -50%) scale(0.65) translate(  3%,  2%); opacity: 0; }
  28%  { transform: translate(-50%, -50%) scale(1.0)  translate( 26%,  5%); opacity: 0.4; }
  65%  { transform: translate(-50%, -50%) scale(1.2)  translate( 58%,  3%); opacity: 0.45; }
  100% { transform: translate(-50%, -50%) scale(1.4)  translate( 84%,  6%); opacity: 0; }
}
@keyframes smoke-pulse-center {
  0%   { transform: translate(-50%, -50%) scale(0.85) translate( 0%,  0%); opacity: 0.25; }
  30%  { transform: translate(-50%, -50%) scale(1.08) translate(-3%, -4%); opacity: 0.5; }
  62%  { transform: translate(-50%, -50%) scale(0.95) translate( 4%, -2%); opacity: 0.4; }
  100% { transform: translate(-50%, -50%) scale(0.85) translate( 0%,  0%); opacity: 0.25; }
}

/* Show-on-mount: the cloud drifts in from invisible over ~1.2s when a
   track is set so it never pops on. */
.smoke-fade-enter-active { transition: opacity 1.2s ease; }
.smoke-fade-leave-active { transition: opacity 0.6s ease; }
.smoke-fade-enter-from,
.smoke-fade-leave-to { opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  .smoke-blob { animation: none !important; }
  .player-smoke { opacity: 0.55; }
}

.shop-player {
  position: relative;
  width: 100%;
  /* 10 % wider than the shop layout's content frame (which caps at
     1440 - 2*24 = 1392px effective). 1392 * 1.1 = 1531.2 → 1532px,
     so the player visibly extends past the page-content edges on
     wide viewports. Narrower viewports stay clamped by the shell's
     gutters below. */
  max-width: 1532px;
  height: 76px;
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(0, 2.4fr) auto;
  align-items: center;
  gap: 24px;
  padding: 10px 18px 10px 10px;
  border-radius: 10px;
  /* Always a dark gradient — the player chrome no longer takes on
     the cover colour. Only the gradient ring (`::before`) carries the
     cover-driven tint; the body of the bar stays a near-black diagonal
     so a vivid cover doesn't wash the controls out. */
  background:
    linear-gradient(135deg, rgba(15, 17, 26, 0.96) 0%, rgba(6, 7, 12, 0.98) 65%, rgba(2, 3, 7, 0.99) 100%);
  /* Subtle white edge — the cover-tinted ring sits over this on the
     ::before, so the base border just keeps the silhouette readable
     when the ring's mid-stops are low-saturation. */
  border: 1px solid rgba(255, 255, 255, 0.08);
  /* The body is now opaque, so `backdrop-filter` is mostly redundant.
     Keep a low blur so the ambient artwork bleed (still mounted for
     the smoke-area context behind the bar) doesn't look like a hard
     edge if the user's browser shows a sliver of it under transitions. */
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  /* Drop-shadow is now a two-stop fall — a tight close shadow for the
     "lifted card" read plus a wider, softer one for ground separation
     from the page background. The inset highlight + bottom darken stay
     so the glass still has interior shape. */
  box-shadow:
    0 6px 14px -4px rgba(0, 0, 0, 0.55),
    0 30px 60px -18px rgba(0, 0, 0, 0.85),
    0 2px 0 0 rgba(255, 255, 255, 0.06) inset,
    0 -1px 0 0 rgba(0, 0, 0, 0.5) inset;
  pointer-events: auto;
  overflow: hidden;
  transition: box-shadow 0.4s ease;
}

/* Vue <Transition> classes — slide-in/out is owned by Vue so the
   v-if mount/unmount cycle plays the keyframes cleanly. */
.player-slide-enter-active {
  transition: transform 0.55s cubic-bezier(0.22, 1.2, 0.36, 1),
              opacity 0.35s ease;
}
.player-slide-leave-active {
  transition: transform 0.35s cubic-bezier(0.4, 0, 1, 1),
              opacity 0.25s ease;
}
.player-slide-enter-from,
.player-slide-leave-to {
  transform: translateY(calc(100% + 24px));
  opacity: 0;
}

.shop-player-shell.playing .shop-player {
  /* Playing state — neutral darker shadow, no coloured halo. The
     cover colour is carried entirely by the border (::before) and the
     surrounding smoke; the player body stays in its dark-gradient lane
     regardless of what artwork is loaded. */
  box-shadow:
    0 10px 22px -4px rgba(0, 0, 0, 0.65),
    0 38px 78px -18px rgba(0, 0, 0, 0.92),
    0 2px 0 0 rgba(255, 255, 255, 0.08) inset,
    0 -1px 0 0 rgba(0, 0, 0, 0.5) inset;
}

/* Hairline gradient border drawn on a pseudo so it can sit above the
   glass without inflating the box. Uses mask-composite to punch the
   center out — the result is a 1px gradient ring that survives blur. */
.shop-player::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  /* Bumped 1px → 1.5px and saturated the gradient stops so the ring
     reads as a deliberate outline rather than a hairline. Also keyed
     to the cover accent so the player's "frame" colour follows the
     artwork along with the visualizer + glow. */
  padding: 1.5px;
  background: linear-gradient(
    135deg,
    rgb(var(--player-accent-channels, var(--primary-channels, 0 242 255)) / 0.75),
    rgba(255, 255, 255, 0.18) 35%,
    rgba(255, 255, 255, 0.06) 65%,
    rgb(var(--player-accent-channels, var(--primary-channels, 0 242 255)) / 0.5)
  );
  -webkit-mask:
    linear-gradient(#000, #000) content-box,
    linear-gradient(#000, #000);
  -webkit-mask-composite: xor;
          mask-composite: exclude;
  pointer-events: none;
  opacity: 1;
  transition: background 0.4s ease;
}

/* ── HUD corner brackets ──────────────────────────────────────────── */
.player-bracket {
  position: absolute;
  width: 14px;
  height: 14px;
  z-index: 2;
  pointer-events: none;
  border: 0 solid rgb(var(--player-accent-channels, var(--primary-channels, 0 242 255)) / 0.35);
  transition: border-color 0.4s ease;
}
.player-bracket--tr { top: 5px; right: 5px; border-top-width: 2px; border-right-width: 2px; }
.player-bracket--bl { bottom: 5px; left: 5px; border-bottom-width: 2px; border-left-width: 2px; }
.shop-player-shell.playing .player-bracket {
  border-color: rgb(var(--player-accent-channels, var(--primary-channels, 0 242 255)) / 0.85);
}

/* ── Ambient: blurred artwork bleed ───────────────────────────────── */
/* The player body is now an opaque dark gradient, so the artwork bleed
   is fully covered. Markup stays in place (cheap to keep) but the
   layer is hidden — restoring the cover-tinted glass is a one-line
   `display: block` if we ever want it back. */
.player-ambient {
  position: absolute;
  inset: -40%;
  z-index: 0;
  pointer-events: none;
  opacity: 0.55;
  display: none;
}

.player-ambient img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Lower blur radius lets actual cover hues survive through the
     outer backdrop-filter, otherwise the ambient layer is just a
     uniform grey behind the glass. */
  filter: blur(28px) saturate(1.9);
  transform: scale(1.3);
}

.player-ambient-veil {
  position: absolute;
  inset: 0;
  /* Veil opacity dropped (0.85 → 0.55 at edges) so artwork color
     actually bleeds through the glass. The corner near the artwork is
     left most exposed so the cover-driven tint reads strongest there. */
  background:
    radial-gradient(circle at 10% 50%, rgba(8, 9, 14, 0.1) 0%, rgba(8, 9, 14, 0.55) 75%),
    linear-gradient(180deg, rgba(8, 9, 14, 0.18) 0%, rgba(8, 9, 14, 0.45) 100%);
}

/* ── Aura: outer glow ring that pulses while playing ───────────────── */
.player-aura {
  position: absolute;
  inset: -2px;
  border-radius: inherit;
  pointer-events: none;
  background: radial-gradient(
    60% 80% at 50% 110%,
    rgb(var(--primary-channels, 0 242 255) / 0.0),
    transparent 60%
  );
  opacity: 0;
  transition: opacity 0.6s ease;
}

.shop-player-shell.playing .player-aura {
  opacity: 1;
  animation: aura-pulse 3.6s ease-in-out infinite;
}

@keyframes aura-pulse {
  0%, 100% {
    background: radial-gradient(60% 80% at 50% 110%, rgb(var(--primary-channels, 0 242 255) / 0.18), transparent 60%);
  }
  50% {
    background: radial-gradient(60% 80% at 50% 110%, rgb(var(--primary-channels, 0 242 255) / 0.32), transparent 65%);
  }
}

/* ── Track: artwork + title ────────────────────────────────────────── */
.player-track {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.player-art {
  position: relative;
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  /* Squared with a rim — the shop has no rounded tiles. */
  border-radius: 0;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.04);
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.5),
    0 0 0 1px var(--cy-rim, rgba(255, 255, 255, 0.06)) inset;
}

.player-art img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.player-art-fallback {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.3);
}

.player-meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.player-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.player-title {
  font-family: var(--cy-display);
  font-size: 0.86rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.96);
  letter-spacing: 0.005em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.player-artist {
  /* Mono, tracked, uppercase — the shop's micro-label voice. This line
     carries "Preview" / the artist, i.e. status rather than content, and
     the title above it already speaks in the display face. (The property,
     not Tailwind's .uppercase class, so refined-look leaves it alone.) */
  font-family: var(--cy-mono, ui-monospace, monospace);
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  /* Bumped 0.5 → 0.65 so contrast holds against the ambient tint
     (some covers light up the glass enough to flatten the 0.5 line). */
  color: rgba(255, 255, 255, 0.65);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Mini EQ — only animates when playing. */
.player-eq {
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  height: 12px;
  flex-shrink: 0;
}

.player-eq span {
  width: 2px;
  height: 3px;
  background: rgb(var(--primary-channels, 0 242 255));
  border-radius: 1px;
  opacity: 0.35;
}

.player-eq.on span {
  opacity: 1;
  animation: eq-bounce 0.9s ease-in-out infinite;
}

.player-eq.on span:nth-child(1) { animation-delay: 0s;    height: 9px; }
.player-eq.on span:nth-child(2) { animation-delay: 0.18s; height: 5px; }
.player-eq.on span:nth-child(3) { animation-delay: 0.36s; height: 11px; }
.player-eq.on span:nth-child(4) { animation-delay: 0.12s; height: 6px; }

@keyframes eq-bounce {
  0%, 100% { transform: scaleY(0.45); }
  50%      { transform: scaleY(1); }
}

/* ── Main: controls + waveform ─────────────────────────────────────── */
.player-main {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.player-controls {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* Console keys, not pills. The storefront's buttons are notched on two
   opposing corners with a hairline rim that ignites on hover; a row of
   circles was the loudest thing on the player that did not belong to
   that language. */
.ctrl {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: rgba(255, 255, 255, 0.05);
  box-shadow: inset 0 0 0 1px var(--cy-rim, rgba(255, 255, 255, 0.12));
  color: rgba(255, 255, 255, 0.65);
  width: 32px;
  height: 32px;
  border-radius: 0;
  --n: 6px;
  clip-path: polygon(
    var(--n) 0, 100% 0,
    100% calc(100% - var(--n)), calc(100% - var(--n)) 100%,
    0 100%, 0 var(--n));
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease, box-shadow 0.25s ease;
}

.ctrl:hover {
  background: rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 0 0 1px var(--cy-rim-hot, rgba(255, 255, 255, 0.4));
  color: rgba(255, 255, 255, 0.95);
}

.ctrl:focus-visible {
  outline: 2px solid rgb(var(--primary-channels, 0 242 255));
  outline-offset: 2px;
}

.ctrl.active {
  background: rgb(var(--primary-channels, 0 242 255) / 0.16);
  color: rgb(var(--primary-channels, 0 242 255));
  box-shadow: 0 0 0 1px rgb(var(--primary-channels, 0 242 255) / 0.35) inset;
}

.ctrl.ghost {
  background: transparent;
}

.ctrl.ghost:hover {
  background: rgba(255, 255, 255, 0.06);
}

/* Queue trigger — active state borrows the cover accent. The pulse
   animation runs once when the composable bumps `historyPulseAt`,
   drawing the eye toward the drawer without nagging when idle. */
.ctrl.queue-btn {
  position: relative;
}
.ctrl.queue-btn.active {
  background: rgb(var(--player-accent-channels, var(--primary-channels, 0 242 255)) / 0.18);
  color: rgb(var(--player-accent-channels, var(--primary-channels, 0 242 255)));
}
.ctrl.queue-btn .queue-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: rgb(var(--player-accent-channels, var(--primary-channels, 0 242 255)));
  color: #0a0b0e;
  font-size: 10px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-shadow: 0 0 0 2px rgba(8, 9, 14, 0.9);
}
.ctrl.queue-btn.pulse::before {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 2px solid rgb(var(--player-accent-channels, var(--primary-channels, 0 242 255)) / 0.55);
  animation: queue-pulse 0.7s ease-out;
  pointer-events: none;
}
@keyframes queue-pulse {
  0%   { transform: scale(0.6); opacity: 0.8; }
  100% { transform: scale(1.4); opacity: 0; }
}

.ctrl.play {
  width: 44px;
  height: 44px;
  /* Bigger notch on the bigger key, matching CyberButton's --cy-notch-sm. */
  --n: 8px;
  background: linear-gradient(135deg, rgb(var(--primary-channels, 0 242 255) / 0.9), rgb(var(--primary-channels, 0 242 255) / 0.7));
  color: #0a0b0e;
  box-shadow:
    0 6px 20px -4px rgb(var(--primary-channels, 0 242 255) / 0.5),
    0 0 0 1px rgb(var(--primary-channels, 0 242 255) / 0.6) inset;
}
.ctrl.play:hover {
  box-shadow:
    var(--cy-glow-hot, 0 6px 24px -4px rgb(var(--primary-channels, 0 242 255) / 0.7)),
    0 0 0 1px rgb(var(--primary-channels, 0 242 255) / 0.8) inset;
}

.ctrl.play:hover {
  transform: scale(1.05);
  filter: brightness(1.08);
}

.ctrl.play .play-ring {
  position: absolute;
  inset: -4px;
  /* Follows the key's cut instead of ringing a circle around a notched
     square, which read as two different shapes fighting. */
  border-radius: 0;
  --n: 9px;
  clip-path: polygon(
    var(--n) 0, 100% 0,
    100% calc(100% - var(--n)), calc(100% - var(--n)) 100%,
    0 100%, 0 var(--n));
  border: 1px solid rgb(var(--primary-channels, 0 242 255) / 0.5);
  opacity: 0;
  pointer-events: none;
}

.shop-player-shell.playing .ctrl.play .play-ring {
  opacity: 1;
  animation: play-pulse 2.4s ease-out infinite;
}

@keyframes play-pulse {
  0%   { transform: scale(0.85); opacity: 0.7; }
  100% { transform: scale(1.5);  opacity: 0; }
}

/* ── Progress: waveform + time ─────────────────────────────────────── */
.player-progress {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.time-label {
  font-family: 'JetBrains Mono', 'SF Mono', 'Fira Code', monospace;
  font-size: 0.68rem;
  /* 0.45 → 0.6: time labels were near-invisible on bright covers. */
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 0.02em;
  flex-shrink: 0;
  width: 38px;
  text-align: center;
}

.waveform-wrap {
  flex: 1;
  height: 40px;
  min-width: 0;
  position: relative;
}

.waveform-loading {
  width: 100%;
  height: 100%;
  border-radius: 4px;
  background:
    linear-gradient(90deg,
      rgba(255, 255, 255, 0.06) 0%,
      rgba(255, 255, 255, 0.15) 50%,
      rgba(255, 255, 255, 0.06) 100%);
  background-size: 200% 100%;
  animation: wave-shimmer 1.6s linear infinite;
  opacity: 0.4;
}

@keyframes wave-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Extras: volume + close ────────────────────────────────────────── */
.player-extras {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.vol {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px 4px 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid transparent;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.vol:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
}

/* Native range input styled to match the rest of the player. Using a
   real <input type="range"> instead of a click-only div so the slider
   is keyboard reachable (Tab + arrow keys) and announces aria-valuenow. */
.vol-range {
  -webkit-appearance: none;
  appearance: none;
  width: 72px;
  height: 14px;
  background: transparent;
  cursor: pointer;
  margin: 0;
  padding: 0;
  --vol-pct: 100%;
}

.vol-range::-webkit-slider-runnable-track {
  height: 4px;
  /* Squared rail + a fader cap for the thumb: a mixing-desk control
     rather than an iOS pill. */
  border-radius: 0;
  background: linear-gradient(
    to right,
    rgb(var(--primary-channels, 0 242 255)) 0%,
    rgb(var(--primary-channels, 0 242 255)) var(--vol-pct),
    rgba(255, 255, 255, 0.1) var(--vol-pct),
    rgba(255, 255, 255, 0.1) 100%
  );
  box-shadow: 0 0 8px rgb(var(--primary-channels, 0 242 255) / 0.35);
}

.vol-range::-moz-range-track {
  height: 4px;
  border-radius: 0;
  background: rgba(255, 255, 255, 0.1);
}

.vol-range::-moz-range-progress {
  height: 4px;
  border-radius: 0;
  background: rgb(var(--primary-channels, 0 242 255));
  box-shadow: 0 0 8px rgb(var(--primary-channels, 0 242 255) / 0.35);
}

.vol-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 4px;
  height: 14px;
  border-radius: 0;
  background: rgb(var(--primary-channels, 0 242 255));
  margin-top: -5px;
  box-shadow: 0 0 0 2px rgba(8, 9, 14, 0.9), 0 0 8px rgb(var(--primary-channels, 0 242 255) / 0.7);
  transition: transform 0.12s ease;
}

.vol-range::-moz-range-thumb {
  width: 4px;
  height: 14px;
  border: none;
  border-radius: 0;
  background: rgb(var(--primary-channels, 0 242 255));
  box-shadow: 0 0 0 2px rgba(8, 9, 14, 0.9), 0 0 8px rgb(var(--primary-channels, 0 242 255) / 0.7);
  transition: transform 0.12s ease;
}

.vol-range:hover::-webkit-slider-thumb,
.vol-range:focus-visible::-webkit-slider-thumb {
  transform: scale(1.2);
}

.vol-range:hover::-moz-range-thumb,
.vol-range:focus-visible::-moz-range-thumb {
  transform: scale(1.2);
}

.vol-range:focus-visible {
  outline: 2px solid rgb(var(--primary-channels, 0 242 255));
  outline-offset: 4px;
  border-radius: 999px;
}

/* ── Responsive ────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .shop-player {
    height: 70px;
    grid-template-columns: minmax(180px, 1fr) minmax(0, 2fr) auto;
    gap: 16px;
  }
  .player-art { width: 54px; height: 54px; }
  .vol-range { width: 56px; }
}

@media (max-width: 768px) {
  .shop-player-shell {
    padding: 0 12px 12px;
  }
  .shop-player {
    height: 64px;
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 2fr) auto;
    gap: 10px;
    padding: 8px 10px 8px 8px;
    border-radius: 8px;
  }
  .player-art { width: 48px; height: 48px; border-radius: 3px; }
  /* Keep the track meta visible on mobile — title + artist is the most
     useful info; hide the EQ glyph and secondary transport controls
     instead so the title still fits. */
  .player-eq { display: none; }
  .player-title { font-size: 0.78rem; }
  .player-artist { font-size: 0.66rem; }
  .player-controls .loop,
  .player-controls .stop { display: none; }
  .vol { display: none; }
  .time-label { width: 32px; font-size: 0.62rem; }
  .waveform-wrap { height: 32px; }
}

@media (max-width: 480px) {
  .time-label { display: none; }
  .player-progress { gap: 6px; }
  .shop-player {
    height: 60px;
    /* 1fr against 2.2fr left the meta column 38px wide on a 390 phone —
       the title needs 176px, so it rendered as "5th …" and the sub-line
       as "PREV…". The comment one breakpoint up says the meta is "the
       most useful info" on mobile; the columns now agree with it. The
       waveform keeps ~90px, which is still a legible strip at 28px tall,
       and it is decoration here — the title is what tells you what is
       playing. */
    grid-template-columns: minmax(0, 1.6fr) minmax(0, 1.4fr) auto;
    gap: 8px;
    padding: 6px 8px 6px 6px;
    border-radius: 8px;
  }
  .player-art { width: 44px; height: 44px; }
  .player-title { font-size: 0.74rem; }
  .player-artist { font-size: 0.62rem; letter-spacing: 0.05em; }
  /* Drop the close button — phones use the queue/back UI to dismiss
     a track, and removing one chip buys real width for the meta col. */
  .player-extras .ctrl.close { display: none; }
  .player-extras { gap: 2px; }
  .waveform-wrap { height: 28px; }
}

/* Very small phones (≤ 360px) — drop the waveform-wrap label gap, keep
   only the bare essentials (art · title · play · queue). */
@media (max-width: 360px) {
  .shop-player {
    grid-template-columns: minmax(0, 1fr) minmax(40px, auto) auto;
    gap: 6px;
    padding: 4px 6px 4px 4px;
  }
  .player-art { width: 40px; height: 40px; }
  .player-progress { display: none; }
  .queue-badge { font-size: 0.55rem; }
}

/* ── Reduced motion: pause all infinite loops + shorten the slide-in
   so motion-sensitive users get a static bar that simply appears. ── */
@media (prefers-reduced-motion: reduce) {
  .shop-player,
  .player-slide-enter-active,
  .player-slide-leave-active {
    transition: opacity 0.15s linear !important;
  }
  .player-slide-enter-from,
  .player-slide-leave-to {
    transform: none !important;
  }
  .shop-player-shell.playing .player-aura,
  .shop-player-shell.playing .ctrl.play .play-ring,
  .player-eq.on span,
  .waveform-loading {
    animation: none !important;
  }
  /* The visualizer's rAF loop is already gated by `vizMotionAllowed`
     in the script, so the canvas never mounts. The class hide here is
     a belt-and-suspenders guard in case the JS check ever regresses. */
  .player-viz { display: none !important; }
}

/* ── Mobile: keep viz proportional and lighter. ────────────────────── */
@media (max-width: 768px) {
  .player-viz {
    /* Mobile: player at bottom 12px, height 64px → top at 76px.
       Canvas bottom at 56px = 20px overlap behind the player. */
    bottom: 56px;
    width: calc((100% - 24px) * 0.9);
    height: 115px;
  }
}
</style>
